import { get, toString, divide, map, capitalize, head, isNumber } from 'lodash';
import { toPairs } from 'lodash/fp';

import DynamicYieldAPI from 'api/DynamicYield';
import { SLOT_CLICK } from 'api/DynamicYield/constants';
import OneTrustController from 'controllers/OneTrustController';
import CartLineItem from 'models/CartLineItem';
import CartSelector from 'state/selectors/cart';
import Iterable from 'utils/Iterable';
import Log from 'utils/Log';
import { CURRENCIES } from 'utils/Money';
import Settings from 'utils/Settings';
import StorefrontUtil from 'utils/Storefront';

const getItem = (state, id) => {
  const cartItems = get(state, ['ajaxCart', 'data', 'entities', 'item']);
  return get(cartItems, id);
};

const getCurrentProduct = (state, handle) => get(state, ['pdp', 'products', handle]);

const diffItems = (prevItem, nextItem) => ({
  quantity: nextItem.quantity - prevItem.quantity,
});

const getProductMsrp = product => product.compareAtPrice || product.price;

const formatPrice = price => {
  if (!isNumber(price)) {
    return null;
  }

  return toString(divide(price, 100));
};

const formatId = id => Number(StorefrontUtil.convertGidtoId(id));

const formatCartItem = (item, quantity) => {
  const { sku, price, priceTier, type, masterProductName, size, color, product } = item;

  return [
    {
      id: sku,
      name: masterProductName,
      price: formatPrice(price),
      category: type,
      variant: `${masterProductName} - ${size} / ${color}`,
      quantity,
      dimension1: size,
      dimension2: color,
      dimension18: formatId(product.id),
      dimension21: priceTier,
      dimension22: formatPrice(getProductMsrp(product)),
    },
  ];
};

const formatPdpProduct = product => {
  const { price, priceTier, type, masterName, sizes, sizesSortOrder, colorName, id } = product;
  const firstSize = head(sizesSortOrder);
  const { sku, value } = get(sizes, firstSize);

  return [
    {
      id: sku,
      name: masterName,
      price: formatPrice(price),
      category: type,
      variant: `${value} / ${colorName}`,
      dimension1: value,
      dimension2: colorName,
      dimension18: formatId(id),
      dimension21: priceTier,
      dimension22: formatPrice(getProductMsrp(product)),
    },
  ];
};

const deriveCartEventFromState = (eventType, currencyCode, products, cartLineItems) => ({
  event: `EEcart${capitalize(eventType)}`,
  ecommerce: {
    currencyCode,
    cartLineItems,
    [eventType]: {
      products,
    },
  },
});

const deriveEventFromState = (itemId, action, prevState, nextState) => {
  const currencyCode = Settings.currency;

  const cartItems = get(nextState, ['ajaxCart', 'data', 'entities', 'item']);
  const items = toPairs(cartItems);
  // eslint-disable-next-line no-unused-vars
  const cartLineItems = map(items, ([_, lineItem]) => {
    const item = new CartLineItem(lineItem);
    return formatCartItem(item, item.quantity)[0];
  }).filter(({ quantity }) => quantity > 0);
  const prevItem = new CartLineItem(getItem(prevState, itemId));
  const nextItem = new CartLineItem(getItem(nextState, itemId));
  const { quantity } = diffItems(prevItem, nextItem);
  const eventType = quantity > 0 ? 'add' : 'remove';
  const products = formatCartItem(quantity > 0 ? nextItem : prevItem, Math.abs(quantity));

  return deriveCartEventFromState(eventType, currencyCode, products, cartLineItems);
};

const legacyAddMultipleLineItemEvent = (action, prevState, nextState) => {
  const items = get(action, ['payload', 'addedItems']);

  return map(items, (quantity, id) => deriveEventFromState(id, action, prevState, nextState));
};

const removeLineItemEvent = (action, prevState, nextState) => {
  const itemId = get(action, ['payload', 'removedItem']);

  return deriveEventFromState(itemId, action, prevState, nextState);
};

const updateLineItemEvent = (action, prevState, nextState) => {
  const itemId = get(action, ['payload', 'updatedItem']);

  return deriveEventFromState(itemId, action, prevState, nextState);
};

const viewProductEvent = (action, prevState, nextState) => {
  return OneTrustController.hasOneTrustLoaded().then(() => {
    const productHandle = get(action, 'payload');
    const nextProduct = getCurrentProduct(nextState, productHandle);
    const products = formatPdpProduct(nextProduct);
    return {
      event: `EEproductView`,
      ecommerce: {
        detail: {
          products,
        },
      },
    };
  });
};

const iterableUpdateCart = (action, prevState, nextState) => {
  const cartItems = CartSelector.getCartLineItems(nextState);
  const currencyCode = Settings.currency;

  const commerceItems = map(cartItems, cartItem => {
    const { sku, price, productTitle, featuredImage, id, product, quantity } = cartItem;

    const variantGid = StorefrontUtil.convertToGid({ id, resourceType: 'ProductVariant' });

    return {
      id: `${id}`,
      sku,
      name: productTitle,
      price: Iterable.formatPriceToFloat(price),
      quantity,
      dataFields: {
        productId: variantGid,
        currency: currencyCode,
        currencySymbol: CURRENCIES[currencyCode].units.symbol,
        variantId: variantGid,
        productImage: get(featuredImage, 'src'),
        handle: get(product, 'handle'),
        source: 'web',
        shopifyStoreDomain: window.location.origin,
        locale: Settings.currentLocale,
        vendor: 'Allbirds',
      },
    };
  });

  Iterable.updateCart({
    items: commerceItems,
  });

  return null;
};

const reportDYRecommendationsClick = action => {
  const slotId = get(action, ['payload', 'slotId']);

  try {
    const clickData = [
      {
        type: SLOT_CLICK,
        slotId,
      },
    ];

    DynamicYieldAPI.reportClick(clickData);
  } catch (error) {
    Log.error(error, `Failed to send DY Engagement Click Report for Recommendations!`);
  }
};

export {
  legacyAddMultipleLineItemEvent,
  removeLineItemEvent,
  updateLineItemEvent,
  viewProductEvent,
  iterableUpdateCart,
  reportDYRecommendationsClick,
};
